// src/pages/EmployeeProfile.js
import React, { useState, useEffect } from 'react';
import {
  Box, Paper, Typography, Grid, Card, CardContent, Avatar,
  Chip, Divider, Tabs, Tab, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Button, Dialog,
  DialogTitle, DialogContent, DialogActions, TextField,
  FormControl, InputLabel, Select, MenuItem, Alert,
  LinearProgress, IconButton
} from '@mui/material';
import {
  Person, Edit, CalendarMonth, TrendingUp, Assignment,
  Work, Schedule, Star, Warning, CheckCircle
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { getData, saveData } from '../services/storageService';
import { useI18n } from '../i18n/i18n';

const EmployeeProfile = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useI18n();
  const [employee, setEmployee] = useState(null);
  const [attendance, setAttendance] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [activeTab, setActiveTab] = useState(0);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [attendanceDialogOpen, setAttendanceDialogOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadEmployeeData();
  }, [id]);

  const loadEmployeeData = async () => {
    setLoading(true);
    try {
      const staffData = await getData('staff') || [];
      const attendanceData = await getData('attendance_records') || [];
      const shiftsData = await getData('shift_records') || [];
      
      const foundEmployee = staffData.find(emp => emp.id == id);
      setEmployee(foundEmployee);
      
      const employeeAttendance = attendanceData.filter(record => record.employeeId == id);
      setAttendance(employeeAttendance);
      
      const employeeShifts = shiftsData.filter(shift => shift.employeeId == id);
      setShifts(employeeShifts);
      
    } catch (error) {
      console.error("Error loading employee data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveEmployee = async (updatedData) => {
    try {
      const staffData = await getData('staff') || [];
      const updatedStaff = staffData.map(emp => 
        emp.id == id ? { ...emp, ...updatedData } : emp
      );
      await saveData('staff', updatedStaff);
      setEmployee({ ...employee, ...updatedData });
      setEditDialogOpen(false);
    } catch (error) {
      console.error("Error saving employee:", error);
    }
  };

  const handleMarkAttendance = async (attendanceData) => {
    try {
      const attendanceRecords = await getData('attendance_records') || [];
      const newRecord = {
        id: Date.now(),
        employeeId: parseInt(id),
        date: new Date().toISOString().split('T')[0],
        status: attendanceData.status,
        reason: attendanceData.reason || '',
        markedBy: 'supervisor', // or get current user
        createdAt: new Date().toISOString()
      };
      
      attendanceRecords.push(newRecord);
      await saveData('attendance_records', attendanceRecords);
      setAttendance(prev => [...prev, newRecord]);
      setAttendanceDialogOpen(false);
    } catch (error) {
      console.error("Error marking attendance:", error);
    }
  };

  // Статистика
  const stats = {
    totalShifts: shifts.length,
    presentDays: attendance.filter(a => a.status === 'present').length,
    absentDays: attendance.filter(a => a.status === 'absent').length,
    lateDays: attendance.filter(a => a.status === 'late').length,
    attendanceRate: attendance.length > 0 ? 
      Math.round((attendance.filter(a => a.status === 'present').length / attendance.length) * 100) : 0
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <LinearProgress sx={{ width: '50%' }} />
      </Box>
    );
  }

  if (!employee) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        {t('employee_profile.not_found')}
      </Alert>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      {/* Заголовок та основна інформація */}
      <Card sx={{ mb: 3, borderRadius: 3 }}>
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
              <Avatar sx={{ width: 80, height: 80, bgcolor: 'primary.main', fontSize: '2rem' }}>
                {employee.name?.[0] || employee.firstName?.[0]}
              </Avatar>
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                  {employee.name || `${employee.firstName} ${employee.lastName}`}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                  <Chip label={t(`roles.${employee.role}`)} color="primary" variant="outlined" />
                  <Chip 
                    label={employee.status === 'active' ? t('employee_profile.active') : t('employee_profile.inactive')} 
                    color={employee.status === 'active' ? 'success' : 'default'} 
                  />
                </Box>
                <Typography variant="body1" color="text.secondary">
                  {employee.department} • {employee.position}
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button 
                variant="outlined" 
                startIcon={<Edit />}
                onClick={() => setEditDialogOpen(true)}
              >
                {t('common.edit')}
              </Button>
              <Button 
                variant="contained" 
                startIcon={<CheckCircle />}
                onClick={() => setAttendanceDialogOpen(true)}
              >
                {t('employee_profile.mark_attendance')}
              </Button>
            </Box>
          </Box>

          {/* Статистика */}
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} md={3}>
              <Card variant="outlined" sx={{ textAlign: 'center', p: 2 }}>
                <Typography variant="h4" color="primary" sx={{ fontWeight: 700 }}>
                  {stats.attendanceRate}%
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('employee_profile.attendance_rate')}
                </Typography>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card variant="outlined" sx={{ textAlign: 'center', p: 2 }}>
                <Typography variant="h4" color="success.main" sx={{ fontWeight: 700 }}>
                  {stats.presentDays}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('employee_profile.present_days')}
                </Typography>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card variant="outlined" sx={{ textAlign: 'center', p: 2 }}>
                <Typography variant="h4" color="error.main" sx={{ fontWeight: 700 }}>
                  {stats.absentDays}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('employee_profile.absent_days')}
                </Typography>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card variant="outlined" sx={{ textAlign: 'center', p: 2 }}>
                <Typography variant="h4" color="warning.main" sx={{ fontWeight: 700 }}>
                  {stats.totalShifts}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('employee_profile.total_shifts')}
                </Typography>
              </Card>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Таби з детальною інформацією */}
      <Paper sx={{ borderRadius: 3 }}>
        <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)} sx={{ px: 3, pt: 2 }}>
          <Tab icon={<Assignment />} label={t('employee_profile.attendance_tab')} />
          <Tab icon={<Schedule />} label={t('employee_profile.shifts_tab')} />
          <Tab icon={<TrendingUp />} label={t('employee_profile.stats_tab')} />
          <Tab icon={<Work />} label={t('employee_profile.work_tab')} />
        </Tabs>

        <Divider />

        <Box sx={{ p: 3 }}>
          {activeTab === 0 && (
            <AttendanceTab 
              attendance={attendance} 
              onMarkAttendance={() => setAttendanceDialogOpen(true)}
              t={t}
            />
          )}
          {activeTab === 1 && <ShiftsTab shifts={shifts} t={t} />}
          {activeTab === 2 && <StatsTab stats={stats} attendance={attendance} t={t} />}
          {activeTab === 3 && <WorkInfoTab employee={employee} t={t} />}
        </Box>
      </Paper>

      {/* Діалог редагування */}
      <EditEmployeeDialog 
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        employee={employee}
        onSave={handleSaveEmployee}
        t={t}
      />

      {/* Діалог відмітки присутності */}
      <MarkAttendanceDialog
        open={attendanceDialogOpen}
        onClose={() => setAttendanceDialogOpen(false)}
        onSave={handleMarkAttendance}
        t={t}
      />
    </Box>
  );
};

// Компоненти для табів
const AttendanceTab = ({ attendance, onMarkAttendance, t }) => (
  <Box>
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
      <Typography variant="h6">{t('employee_profile.attendance_history')}</Typography>
      <Button variant="contained" onClick={onMarkAttendance}>
        {t('employee_profile.mark_today')}
      </Button>
    </Box>
    
    <TableContainer>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>{t('employee_profile.date')}</TableCell>
            <TableCell>{t('employee_profile.status')}</TableCell>
            <TableCell>{t('employee_profile.reason')}</TableCell>
            <TableCell>{t('employee_profile.marked_by')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {attendance.map(record => (
            <TableRow key={record.id}>
              <TableCell>{new Date(record.date).toLocaleDateString()}</TableCell>
              <TableCell>
                <Chip 
                  label={t(`attendance.status.${record.status}`)}
                  color={
                    record.status === 'present' ? 'success' :
                    record.status === 'absent' ? 'error' :
                    record.status === 'late' ? 'warning' : 'default'
                  }
                  size="small"
                />
              </TableCell>
              <TableCell>{record.reason || '-'}</TableCell>
              <TableCell>{record.markedBy}</TableCell>
            </TableRow>
          ))}
          {attendance.length === 0 && (
            <TableRow>
              <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                <Typography color="text.secondary">
                  {t('employee_profile.no_attendance_records')}
                </Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  </Box>
);

const ShiftsTab = ({ shifts, t }) => (
  <Box>
    <Typography variant="h6" sx={{ mb: 3 }}>{t('employee_profile.shift_history')}</Typography>
    <TableContainer>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>{t('employee_profile.shift_date')}</TableCell>
            <TableCell>{t('employee_profile.shift_type')}</TableCell>
            <TableCell>{t('employee_profile.hours')}</TableCell>
            <TableCell>{t('employee_profile.notes')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {shifts.map(shift => (
            <TableRow key={shift.id}>
              <TableCell>{new Date(shift.date).toLocaleDateString()}</TableCell>
              <TableCell>{shift.type}</TableCell>
              <TableCell>{shift.hours}</TableCell>
              <TableCell>{shift.notes || '-'}</TableCell>
            </TableRow>
          ))}
          {shifts.length === 0 && (
            <TableRow>
              <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                <Typography color="text.secondary">
                  {t('employee_profile.no_shift_records')}
                </Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  </Box>
);

const StatsTab = ({ stats, attendance, t }) => (
  <Grid container spacing={3}>
    <Grid item xs={12} md={6}>
      <Card sx={{ p: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>{t('employee_profile.attendance_overview')}</Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography>{t('employee_profile.present')}:</Typography>
            <Typography fontWeight="bold">{stats.presentDays}</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography>{t('employee_profile.absent')}:</Typography>
            <Typography fontWeight="bold" color="error.main">{stats.absentDays}</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography>{t('employee_profile.late')}:</Typography>
            <Typography fontWeight="bold" color="warning.main">{stats.lateDays}</Typography>
          </Box>
        </Box>
      </Card>
    </Grid>
    <Grid item xs={12} md={6}>
      <Card sx={{ p: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>{t('employee_profile.performance')}</Typography>
        <LinearProgress 
          variant="determinate" 
          value={stats.attendanceRate} 
          sx={{ height: 10, borderRadius: 5, mb: 2 }}
          color={stats.attendanceRate >= 90 ? 'success' : stats.attendanceRate >= 70 ? 'warning' : 'error'}
        />
        <Typography variant="body2" align="center">
          {stats.attendanceRate}% {t('employee_profile.attendance_rate')}
        </Typography>
      </Card>
    </Grid>
  </Grid>
);

const WorkInfoTab = ({ employee, t }) => (
  <Grid container spacing={3}>
    <Grid item xs={12} md={6}>
      <Card sx={{ p: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>{t('employee_profile.work_info')}</Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box>
            <Typography variant="body2" color="text.secondary">{t('employee_profile.department')}</Typography>
            <Typography>{employee.department}</Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">{t('employee_profile.position')}</Typography>
            <Typography>{employee.position}</Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">{t('employee_profile.role')}</Typography>
            <Typography>{t(`roles.${employee.role}`)}</Typography>
          </Box>
        </Box>
      </Card>
    </Grid>
    <Grid item xs={12} md={6}>
      <Card sx={{ p: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>{t('employee_profile.contact_info')}</Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {employee.email && (
            <Box>
              <Typography variant="body2" color="text.secondary">Email</Typography>
              <Typography>{employee.email}</Typography>
            </Box>
          )}
          {employee.phone && (
            <Box>
              <Typography variant="body2" color="text.secondary">{t('employee_profile.phone')}</Typography>
              <Typography>{employee.phone}</Typography>
            </Box>
          )}
        </Box>
      </Card>
    </Grid>
  </Grid>
);

// Діалог редагування
const EditEmployeeDialog = ({ open, onClose, employee, onSave, t }) => {
  const [formData, setFormData] = useState(employee || {});

  const handleSave = () => {
    onSave(formData);
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{t('employee_profile.edit_employee')}</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label={t('employee_profile.first_name')}
              value={formData.firstName || ''}
              onChange={(e) => handleChange('firstName', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label={t('employee_profile.last_name')}
              value={formData.lastName || ''}
              onChange={(e) => handleChange('lastName', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>{t('employee_profile.department')}</InputLabel>
              <Select
                value={formData.department || ''}
                label={t('employee_profile.department')}
                onChange={(e) => handleChange('department', e.target.value)}
              >
                <MenuItem value="General">General</MenuItem>
                <MenuItem value="SECADOR/A Y/O CARGA TÚNEL">SECADOR/A Y/O CARGA TÚNEL</MenuItem>
                <MenuItem value="CONTROL DE TÚNEL">CONTROL DE TÚNEL</MenuItem>
                <MenuItem value="CHÓFER">CHÓFER</MenuItem>
                <MenuItem value="Mantenimiento">Mantenimiento</MenuItem>
                <MenuItem value="Administración">Administración</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label={t('employee_profile.position')}
              value={formData.position || ''}
              onChange={(e) => handleChange('position', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>{t('employee_profile.role')}</InputLabel>
              <Select
                value={formData.role || ''}
                label={t('employee_profile.role')}
                onChange={(e) => handleChange('role', e.target.value)}
              >
                <MenuItem value="operator">{t('roles.operator')}</MenuItem>
                <MenuItem value="technician">{t('roles.technician')}</MenuItem>
                <MenuItem value="supervisor">{t('roles.supervisor')}</MenuItem>
                <MenuItem value="admin">{t('roles.admin')}</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>{t('employee_profile.status')}</InputLabel>
              <Select
                value={formData.status || 'active'}
                label={t('employee_profile.status')}
                onChange={(e) => handleChange('status', e.target.value)}
              >
                <MenuItem value="active">{t('employee_profile.active')}</MenuItem>
                <MenuItem value="inactive">{t('employee_profile.inactive')}</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={formData.email || ''}
              onChange={(e) => handleChange('email', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label={t('employee_profile.phone')}
              type="tel"
              value={formData.phone || ''}
              onChange={(e) => handleChange('phone', e.target.value)}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.cancel')}</Button>
        <Button onClick={handleSave} variant="contained">
          {t('common.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Діалог відмітки присутності
const MarkAttendanceDialog = ({ open, onClose, onSave, t }) => {
  const [status, setStatus] = useState('present');
  const [reason, setReason] = useState('');

  const handleSave = () => {
    onSave({ status, reason });
    setStatus('present');
    setReason('');
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>{t('employee_profile.mark_attendance')}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1, minWidth: 300 }}>
          <FormControl fullWidth>
            <InputLabel>{t('employee_profile.status')}</InputLabel>
            <Select
              value={status}
              label={t('employee_profile.status')}
              onChange={(e) => setStatus(e.target.value)}
            >
              <MenuItem value="present">{t('attendance.status.present')}</MenuItem>
              <MenuItem value="absent">{t('attendance.status.absent')}</MenuItem>
              <MenuItem value="late">{t('attendance.status.late')}</MenuItem>
              <MenuItem value="vacation">{t('attendance.status.vacation')}</MenuItem>
              <MenuItem value="sick">{t('attendance.status.sick')}</MenuItem>
            </Select>
          </FormControl>
          
          {(status === 'absent' || status === 'late') && (
            <TextField
              fullWidth
              label={t('employee_profile.reason')}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              multiline
              rows={3}
            />
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.cancel')}</Button>
        <Button onClick={handleSave} variant="contained">
          {t('common.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EmployeeProfile;